import { observable, action, computed, reaction } from 'mobx';

import { onClanChanged, onClanInvitesChanged } from '../lib/dbCalls';
import { ClanType, ClanInvitesType, ClanInviteType, ClanMemberType } from '../lib/types';

import heroStore from './hero';

type ClanMemberRow = ClanMemberType & { id: string };

class Clans {
  @observable
  clan: ClanType | null = null;

  @observable
  invites: ClanInvitesType = [];

  clanChangedRef: any = null;
  invitesChangedRef: any = null;
  disposeReaction: any = null;

  listenChanges(warriorId: string) {
    // Invites are keyed by the warrior and stay subscribed regardless of membership.
    this.invitesChangedRef = onClanInvitesChanged(warriorId, this.onInvitesChanged);

    // The clan subscription follows hero.clanId, which changes on join/leave/kick.
    this.syncClan(heroStore.hero.clanId);
    this.disposeReaction = reaction(
      () => heroStore.hero && heroStore.hero.clanId,
      (clanId) => this.syncClan(clanId),
    );
  }

  syncClan(clanId?: string | null) {
    if (this.clanChangedRef) {
      this.clanChangedRef.off('value', this.onClanChanged);
      this.clanChangedRef = null;
    }
    if (!clanId) {
      this.setClan(null);
      return;
    }
    this.clanChangedRef = onClanChanged(clanId, this.onClanChanged);
  }

  @action
  setClan(clan: ClanType | null) {
    this.clan = clan;
  }

  @action
  onClanChanged = (snapshot: any) => {
    this.clan = snapshot.val() || null;
  };

  @action
  onInvitesChanged = (snapshot: any) => {
    const val = snapshot.val();
    this.invites = val ? (Object.values(val) as ClanInviteType[]) : [];
  };

  @computed
  get isLeader(): boolean {
    return Boolean(this.clan && heroStore.hero && this.clan.leaderId === heroStore.hero.id);
  }

  @computed
  get members(): ClanMemberRow[] {
    if (!this.clan || !this.clan.members) return [];
    return Object.entries(this.clan.members)
      .map(([id, member]) => ({ id, ...member }))
      .sort((a, b) => a.joinedAt - b.joinedAt);
  }

  @computed
  get memberCount(): number {
    return this.clan && this.clan.members ? Object.keys(this.clan.members).length : 0;
  }

  @computed
  get currentWar() {
    return this.clan ? this.clan.currentWar || null : null;
  }

  @computed
  get inviteCount(): number {
    return this.invites.length;
  }

  // Clear the store and re-establish subscriptions (Firebase re-delivers current
  // values immediately on a fresh .on), giving a clean sync when the modal opens.
  refresh() {
    this.clear();
    if (heroStore.hero) {
      this.listenChanges(heroStore.hero.id);
    }
  }

  clear() {
    if (this.disposeReaction) {
      this.disposeReaction();
      this.disposeReaction = null;
    }
    if (this.clanChangedRef) {
      this.clanChangedRef.off('value', this.onClanChanged);
      this.clanChangedRef = null;
    }
    if (this.invitesChangedRef) {
      this.invitesChangedRef.off('value', this.onInvitesChanged);
      this.invitesChangedRef = null;
    }
    this.clan = null;
    this.invites = [];
  }
}

export default new Clans();
